import { Icon } from "@cabocheck/shared";
import { ChevronDown, PrinterIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { assets } from "~/assets";
import type { MedicalTestProfileTranslated } from "~/utils/apis/loadProducts.server";
import { CaboMarkdown } from "../core/CaboMarkdown";
import { Button } from "~/components/ui/button";
import { formatPrice } from "~/utils/formatPrice";

export function ProductBanner({
  product,
  defaultExpanded = false,
  showPrice = false,
  showPrint = false,
}: {
  product: MedicalTestProfileTranslated;
  defaultExpanded?: boolean;
  showPrice?: boolean;
  showPrint?: boolean;
}) {
  const { t } = useTranslation();
  const sampleTypeKeyMap = {
    BLOOD: t("Kapillarblut in der Apotheke"),
    SALIVA: t("Speichelprobe "),
    URINE: t("Urinprobe "),
  } as const;

  const sampleIconMap = {
    BLOOD: assets["./icons/BLOOD.svg"]!,
    SALIVA: assets["./icons/URINE.svg"]!,
    URINE: assets["./icons/URINE.svg"]!,
  } as const;

  const [expanded, setExpanded] = useState(defaultExpanded);
  const toggleExpand = () => {
    setExpanded(!expanded);
  };
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (textRef.current) {
      const markdown = textRef.current.children[0];
      if (!markdown) return;
      setIsOverflowing(markdown.scrollHeight > markdown.clientHeight);
    }
  }, []);

  const kinds = Array.from(new Set(product.medical_tests.map((t) => t.specimen_type)));

  return (
    <section className="grid items-center pb-8 pt-16 print:p-0">
      {/* <div className="flex flex-col justify-center items-center"> */}
      {/*   {product.url ? ( */}
      {/*     <img src={product.url} alt={product.name} className="w-2/3 aspect-[2]" /> */}
      {/*   ) : product.image_url ? ( */}
      {/*     <img src={product.image_url} alt={product.name} className="w-full" /> */}
      {/*   ) : null} */}
      {/* </div> */}
      <div className="grid gap-5">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="font-medium text-dynamic-primary">{product.translated.info?.subline}</p>
            <h1 className="font-bold">{product.name}</h1>
            <ul className="flex flex-wrap gap-2">
              {product.tags?.map((tag) => (
                <li key={tag.medical_test_profile_tag_guid} className="flex gap-2">
                  <span>{tag.name}</span>
                </li>
              ))}
            </ul>
            {showPrice && product.selling_price ? (
              <p className="text-xl font-bold">{formatPrice(product.selling_price)} €</p>
            ) : null}
          </div>
          {showPrint ? (
            <Button className="print:hidden" onClick={() => window.print()}>
              <PrinterIcon className="mr-2 h-4 w-4" />
              <small>{t("Drucken")}</small>
            </Button>
          ) : null}
        </div>
        <div ref={textRef} className="relative">
          <CaboMarkdown className={`${expanded ? "" : "line-clamp-6"} relative pr-16`}>
            {product.translated.info?.description}
          </CaboMarkdown>
          <button className="absolute bottom-0.5 right-4 bg-white text-sm text-blue-550" onClick={() => toggleExpand()}>
            {isOverflowing && !expanded ? (
              <div className="flex items-center justify-end gap-0">
                <span>{t("Mehr")}</span>
                <ChevronDown className="h-4 w-4" />
              </div>
            ) : null}
          </button>
        </div>
        {kinds.map((kind) => (
          <div key={kind} className="flex gap-2 bg-[#E6F0F8] p-4">
            <Icon svg={sampleIconMap[kind]} />
            <b className="flex-1">{sampleTypeKeyMap[kind]}</b>
          </div>
        ))}
      </div>
    </section>
  );
}
